import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { NAV_ITEMS } from "../../router/navigation";
import { useOverlayFocus } from "../base/useOverlayFocus";
import { COMMAND_PALETTE_OPEN_EVENT } from "./commandPaletteBus";

interface Command {
  id: string;
  label: string;
  hint?: string;
  icon: string;
  run: (navigate: ReturnType<typeof useNavigate>) => void;
  haystack: string;
}

export function CommandPalette() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const close = () => setOpen(false);
  useOverlayFocus(open, close, containerRef);

  // Global open: Cmd/Ctrl+K and the custom event from the TopBar button.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setOpen((value) => !value);
      }
    };
    const onOpen = () => setOpen(true);
    window.addEventListener("keydown", onKey);
    window.addEventListener(COMMAND_PALETTE_OPEN_EVENT, onOpen);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener(COMMAND_PALETTE_OPEN_EVENT, onOpen);
    };
  }, []);

  useEffect(() => {
    if (open) {
      setQuery("");
      setActiveIndex(0);
      window.setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  const commands = useMemo<Command[]>(() => {
    const navCommands: Command[] = NAV_ITEMS.map((item) => ({
      id: `nav:${item.path}`,
      label: item.label,
      hint: "Go to",
      icon: item.icon,
      run: (nav) => nav(item.path),
      haystack: `${item.label} ${item.keywords ?? ""}`.toLowerCase(),
    }));
    const trimmed = query.trim();
    if (trimmed) {
      navCommands.push({
        id: "action:find-device",
        label: `Find device by id “${trimmed}”`,
        hint: "Devices",
        icon: "ri-search-line",
        run: (nav) => nav(`/devices?customer_id=${encodeURIComponent(trimmed)}`),
        haystack: `find device search ${trimmed}`.toLowerCase(),
      });
    }
    return navCommands;
  }, [query]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return commands;
    return commands.filter((command) =>
      q.split(/\s+/u).every((token) => command.haystack.includes(token)),
    );
  }, [commands, query]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  if (!open) return null;

  const run = (command: Command | undefined) => {
    if (!command) return;
    setOpen(false);
    command.run(navigate);
  };

  const onInputKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((index) => Math.min(index + 1, results.length - 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((index) => Math.max(index - 1, 0));
    } else if (event.key === "Enter") {
      event.preventDefault();
      run(results[activeIndex]);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-slate-900/30 flex items-start justify-center pt-32 z-50"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) close();
      }}
    >
      <div
        ref={containerRef}
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        className="w-[560px] max-w-[90vw] bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden"
      >
        <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100">
          <i className="ri-search-line text-slate-400" />
          <input
            ref={inputRef}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={onInputKeyDown}
            placeholder="Jump to a section or find by id…"
            aria-label="Command palette search"
            className="flex-1 text-sm focus:outline-none placeholder-slate-400"
          />
          <kbd className="font-mono text-[10px] bg-slate-100 px-1 rounded text-slate-400">esc</kbd>
        </div>
        <div className="py-2 text-sm max-h-72 overflow-y-auto">
          {results.length === 0 && (
            <p className="px-4 py-6 text-center text-slate-400 text-xs">No matches</p>
          )}
          {results.map((command, index) => (
            <button
              key={command.id}
              type="button"
              onMouseEnter={() => setActiveIndex(index)}
              onClick={() => run(command)}
              className={`w-full px-4 py-1.5 flex items-center gap-2 text-left cursor-pointer ${
                index === activeIndex ? "bg-coral-50 text-coral-700" : "hover:bg-slate-50 text-slate-700"
              }`}
            >
              <i className={`${command.icon} text-base shrink-0`} />
              <span className="flex-1 truncate">{command.label}</span>
              {command.hint && <span className="text-[10px] text-slate-400 uppercase tracking-wide">{command.hint}</span>}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
